import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Image, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FileUpload({ onUploaded, token, accept, label }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };

      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try { reject(new Error(JSON.parse(xhr.responseText).detail)); }
            catch { reject(new Error(`Upload failed (${xhr.status})`)); }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      setUploaded({ name: file.name, size: file.size, url: result.url });
      setProgress(100);
      if (onUploaded) onUploaded(result.url, result);
    } catch (err) {
      setError(err.message || 'Upload failed');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  }, [token, onUploaded]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const clear = () => {
    setUploaded(null);
    setProgress(0);
    setError('');
    if (onUploaded) onUploaded('', null);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const isImage = uploaded?.name && /\.(jpg|jpeg|png)$/i.test(uploaded.name);

  return (
    <div data-testid="file-upload">
      {!uploaded ? (
        <div
          data-testid="file-upload-dropzone"
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[#1B5486] bg-blue-50' : error ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-400 bg-slate-50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" className="hidden" accept={accept || '.jpg,.jpeg,.png,.pdf,.xlsx,.doc,.docx'} onChange={onSelect} />
          {uploading ? (
            <div className="space-y-2">
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div className="bg-[#1B5486] h-full rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-slate-500">Uploading... {progress}%</p>
            </div>
          ) : (
            <>
              <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
              <p className="text-xs text-slate-600">{label || 'Drop file here or click to browse'}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Max 10MB. JPG, PNG, PDF, XLSX, DOC</p>
            </>
          )}
          {error && (
            <div className="flex items-center justify-center gap-1 mt-2 text-xs text-red-600">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 flex items-center gap-3" data-testid="file-upload-preview">
          {isImage ? <Image className="w-5 h-5 text-[#1B5486] flex-shrink-0" /> : <FileText className="w-5 h-5 text-[#1B5486] flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-800 truncate">{uploaded.name}</p>
            <p className="text-[10px] text-slate-400">{formatSize(uploaded.size)}</p>
          </div>
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <button type="button" onClick={clear} className="text-slate-400 hover:text-red-500 flex-shrink-0" data-testid="file-upload-clear">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
